import { Box, Icon, Pressable, Progress, ProgressFilledTrack, Typography } from "components/ui";
import { useNavigation, useSegments, useLocalSearchParams } from "expo-router";
import { ArrowRightFromLine, Check, CreditCard, ReceiptText, Truck } from "lucide-react-native";
import React, { useEffect, useState } from "react";
import { Touchable, View } from "react-native";
import { useIsMobile } from "utils/hooks/useIsMobile";

type Props = {
	isApprover?: boolean;
	value?: number | null;
	isLinkDisabled?: boolean;
};

const CheckoutProgress = ({ isApprover, value, isLinkDisabled }: Props) => {
	function extractIdFromSegment(segment: string) {
		const match = segment.match(/\d+/);
		return match ? parseInt(match[0], 10) : 1; // Default to step 1 if no number found
	}

	const segments = useSegments();
	const part = segments[segments.length - 1];
	const [currentStep, setCurrentStep]: any = useState(value ?? extractIdFromSegment(part));
	const isMobile = useIsMobile();
	const navigation = useNavigation();
	const { id } = useLocalSearchParams<{ id: string }>();

	useEffect(() => {
		if (value !== undefined && value !== null) {
			setCurrentStep(value);
		} else {
			setCurrentStep(extractIdFromSegment(part));
		}
	}, [part, value]);

	function doNavigation(stepId: number): void {
		const path = `cart/(step${stepId})/index`;
		if (id) {
			// @ts-ignore
			navigation.navigate(path, { id });
		} else {
			// @ts-ignore
			navigation.navigate(path);
		}
	}

	const checkoutProgress = [
		{ title: isMobile ? "Review" : "Review Order", icon: ReceiptText, id: 1 },
		{ title: "Delivery", icon: Truck, id: 2 },
		{ title: "Payment", icon: CreditCard, id: 3 },
		{ title: isMobile ? (isApprover ? "Approve" : "Submit") : isApprover ? "Approve & Submit" : "Complete", icon: ArrowRightFromLine, id: 4 },
	];

	return (
		<View>
			<Box marginTop={isMobile ? 0 : 20} marginBottom={isMobile ? 0 : 20} borderRadius={12} paddingHorizontal={isMobile ? 16 : 32} paddingVertical={24} bg={"$neutral25"}>
				<Box flexDirection="row" marginBottom={isMobile ? 8 : 16} justifyContent="space-around">
					{checkoutProgress.map((step, index) => {
						const PressableStep = ({ children }: any) => {
							if (currentStep > step.id && !isLinkDisabled) {
								return (
									<Pressable alignItems="center" justifyContent="center" aspectRatio={1} width={100} height={20} onPress={() => doNavigation(step.id)}>
										{children}
									</Pressable>
								);
							}

							return <>{children}</>;
						};

						return (
							<Box key={index} flex={1} alignItems="center" justifyContent="center">
								<PressableStep>
									<Box
										backgroundColor={currentStep > step.id ? "$primarylightblue" : ""}
										alignItems="center"
										justifyContent="center"
										borderWidth={1}
										aspectRatio={1}
										width={40}
										borderColor={currentStep === step.id ? "$primarylightblue" : "$neutral100"}
										borderRadius={"$full"}
									>
										{currentStep > step.id ? (
											<Icon as={Check} color={currentStep === step.id ? "$primarylightblue" : "$neutral100"} aspectRatio={1} width={24} />
										) : (
											<Icon as={step.icon} color={currentStep === step.id ? "$primarylightblue" : "$neutral100"} aspectRatio={1} width={24} />
										)}
									</Box>
									<Typography
										sx={{
											color: currentStep === step.id || currentStep > step.id ? "$basicblack" : "$neutral100",
											fontSize: isMobile ? 14 : 16,
										}}
										weight="bold"
									>
										{step.title}
									</Typography>
								</PressableStep>
							</Box>
						);
					})}
				</Box>
				<Progress bg={"$neutral100"} value={(currentStep / 4) * 100} w={"$full"} size={isMobile ? "md" : "lg"}>
					<ProgressFilledTrack bg="$primarylightblue" />
				</Progress>
			</Box>
		</View>
	);
};

export default CheckoutProgress;
